import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/modal";
import { deleteClient } from "api/graphQL_clients";
import DivLoader from "components/divloader/DivLoader";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { MdDelete } from "react-icons/md";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

const DeleteClientModal = ({
  id,
  fetchClients,
}: {
  id: string;
  fetchClients: () => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [isCloseModal, setIsCloseModal] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);

  const handleClose = () => {
    setConfirmed(false);
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  const handleDelete = () => {
    setIsLoading(true);
    deleteClient(id)
      .then((data) => {
        if (data.status === "Error") {
          toast.error(data?.message ?? "Something went wrong!");
        } else {
          fetchClients();
          toast.success("Client deleted successfully");
          handleClose();
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Client deletion failed"))
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      {/* Trigger icon */}
      <Popover trigger="hover" placement="bottom">
        <PopoverTrigger>
          <button onClick={onOpen} className="dcm-trigger" aria-label="Delete client">
            <MdDelete size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent border="1px solid #fecaca" borderRadius="12px" boxShadow="0 8px 24px rgba(15,23,42,0.08)" bg="#fef2f2" maxW="260px" p="0" overflow="hidden" zIndex="20">
          <PopoverHeader bg="#fee2e2" fontSize="12px" fontWeight="700" color="#b91c1c" borderBottom="1px solid #fecaca" px="14px" py="10px">Delete Client</PopoverHeader>
          <PopoverArrow bg="#fef2f2" />
        </PopoverContent>
      </Popover>

      {/* Modal / slide panel */}
      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay className="bg-[#000] !opacity-40" />

        <div className={`dcm-panel${isOpen && !isCloseModal ? " dcm-panel--open" : " dcm-panel--close"}`}>

          {/* Header */}
          <div className="dcm-header">
            <div className="dcm-header-left">
              <div className="dcm-header-icon">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14H6L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4h6v2"/>
                </svg>
              </div>
              <div>
                <h2 className="dcm-header-title">Delete Client</h2>
                <p className="dcm-header-sub">This action cannot be undone</p>
              </div>
            </div>
            <button className="dcm-close-btn" onClick={handleClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="dcm-body">
            {/* Warning card */}
            <div className="dcm-warning-card">
              <div className="dcm-warning-icon-wrap">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div className="dcm-warning-content">
                <p className="dcm-warning-title">Irreversible Action</p>
                <p className="dcm-warning-desc">
                  Deleting this client will permanently remove all associated data including gateway configurations, payment methods, and transaction history. This cannot be recovered.
                </p>
              </div>
            </div>

            {/* Consequence list */}
            <div className="dcm-consequences">
              <p className="dcm-consequences-title">What will be deleted:</p>
              <div className="dcm-consequence-list">
                {[
                  "Client profile and credentials",
                  "All gateway assignments",
                  "Payment method configurations",
                  "Routing rules and settings",
                ].map((item, i) => (
                  <div key={i} className="dcm-consequence-item">
                    <div className="dcm-consequence-dot" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Confirmation checkbox */}
            <label className="dcm-confirm-row">
              <input
                type="checkbox"
                className="dcm-confirm-check"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <span className="dcm-confirm-label">
                I understand this action is permanent and cannot be undone
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="dcm-footer">
            <div className="dcm-footer-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Confirm your intent before proceeding
            </div>
            <div className="dcm-footer-btns">
              <button className="dcm-btn-cancel" onClick={handleClose}>Cancel</button>
              <button
                className={`dcm-btn-delete${confirmed ? "" : " dcm-btn-delete--disabled"}`}
                onClick={handleDelete}
                disabled={!confirmed || !!isLoading}
              >
                {isLoading ? (
                  <span className="dcm-spinner" />
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14H6L5 6"/>
                      <path d="M10 11v6"/><path d="M14 11v6"/>
                    </svg>
                    Delete Client
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* Trigger */
        .dcm-trigger {
          width: 32px; height: 32px; border-radius: 8px;
          background: #fff7f7; border: 1.5px solid #fecaca;
          color: #dc2626; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s;
        }
        .dcm-trigger:hover { background: #fee2e2; border-color: #fca5a5; }

        /* Slide panel */
        .dcm-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 600px;
          background: #ffffff;
          z-index: 1004;
          display: flex; flex-direction: column;
          box-shadow: -8px 0 40px rgba(15,23,42,0.15);
          transition: transform 0.32s cubic-bezier(0.16,1,0.3,1);
        }
        .dcm-panel--open  { transform: translateX(0); }
        .dcm-panel--close { transform: translateX(100%); }

        /* Header */
        .dcm-header {
          padding: 16.5px 28px;
          background: linear-gradient(135deg, #7f1d1d, #dc2626);
          display: flex; align-items: center; justify-content: space-between;
          flex-shrink: 0;
        }
        .dcm-header-left { display: flex; align-items: center; gap: 14px; }
        .dcm-header-icon {
          width: 42px; height: 42px; border-radius: 11px;
          background: rgba(255,255,255,0.18); border: 1.5px solid rgba(255,255,255,0.28);
          display: flex; align-items: center; justify-content: center;
          color: #fff; flex-shrink: 0;
        }
        .dcm-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -0.3px; margin-bottom: 2px;line-height:22px }
        .dcm-header-sub { font-size: 12.5px; color: rgba(255,255,255,0.65); font-weight: 500; }
        .dcm-close-btn {
          background: rgba(255,255,255,0.15); border: none; border-radius: 8px;
          width: 32px; height: 32px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,0.8); transition: background 0.15s;
        }
        .dcm-close-btn:hover { background: rgba(255,255,255,0.25); color: #fff; }

        /* Body */
        .dcm-body { flex: 1; padding: 28px; display: flex; flex-direction: column; gap: 20px; overflow-y: auto; }

        /* Warning card */
        .dcm-warning-card {
          display: flex; gap: 14px;
          background: linear-gradient(135deg, #fff7f7, #fee2e2);
          border: 1.5px solid #fecaca; border-radius: 14px; padding: 18px;
        }
        .dcm-warning-icon-wrap {
          width: 44px; height: 44px; border-radius: 11px;
          background: #fff; border: 1.5px solid #fecaca;
          display: flex; align-items: center; justify-content: center;
          color: #dc2626; flex-shrink: 0;
        }
        .dcm-warning-content { flex: 1; }
        .dcm-warning-title { font-size: 14px; font-weight: 700; color: #991b1b; margin-bottom: 5px; }
        .dcm-warning-desc { font-size: 13px; color: #b91c1c; line-height: 1.55; font-weight: 500; }

        /* Consequences */
        .dcm-consequences {
          background: #fafbfc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px;
        }
        .dcm-consequences-title { font-size: 12.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 12px; }
        .dcm-consequence-list { display: flex; flex-direction: column; gap: 8px; }
        .dcm-consequence-item { display: flex; align-items: center; gap: 10px; font-size: 13.5px; color: #475569; font-weight: 500; }
        .dcm-consequence-dot { width: 6px; height: 6px; border-radius: 50%; background: #ef4444; flex-shrink: 0; }

        /* Confirm checkbox */
        .dcm-confirm-row {
          display: flex; align-items: flex-start; gap: 12px; cursor: pointer;
          background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 12px; padding: 14px;
        }
        .dcm-confirm-check {
          width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px;
          accent-color: #dc2626; cursor: pointer;
        }
        .dcm-confirm-label { font-size: 13px; font-weight: 600; color: #92400e; line-height: 1.5; }

        /* Footer */
        .dcm-footer {
          padding: 16px 28px; border-top: 1px solid #f1f5f9; background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 12px; flex-shrink: 0;
        }
        .dcm-footer-note { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .dcm-footer-btns { display: flex; gap: 10px; }

        .dcm-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13.5px; font-weight: 600; color: #64748b;
          padding: 10px 20px; border-radius: 10px;
          border: 1.5px solid #e2e8f0; background: #fff; cursor: pointer;
          transition: all 0.15s;
        }
        .dcm-btn-cancel:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }

        .dcm-btn-delete {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #991b1b, #dc2626);
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(220,38,38,0.3);
          transition: transform 0.15s, box-shadow 0.18s, opacity 0.18s;
        }
        .dcm-btn-delete:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(220,38,38,0.38); }
        .dcm-btn-delete--disabled { opacity: 0.45; cursor: not-allowed; transform: none !important; box-shadow: none !important; }

        .dcm-spinner {
          display: inline-block; width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.35); border-top-color: #fff;
          border-radius: 50%; animation: dcmSpin 0.65s linear infinite;
        }
        @keyframes dcmSpin { to { transform: rotate(360deg); } }

        @media (max-width: 520px) {
          .dcm-panel { max-width: 100%; }
          .dcm-header, .dcm-body, .dcm-footer { padding-left: 20px; padding-right: 20px; }
        }
      `}</style>
    </>
  );
};

export default DeleteClientModal;